import { computed, type Ref } from 'vue';
import type {
  RdoAtividade,
  RdoAtividadePhase,
  RdoScheduleActivity,
  RdoAtividadeSituacao,
  RdoTaskBreakdownEntry,
} from '@/modules/field/types/rdo-form';
import { todayIsoDateLocal } from '@/modules/field/utils/dates';
import { generateFieldId } from '@/modules/field/utils/generateId';
import { calculateParentCompletion } from '@/modules/field/utils/scheduleParentCompletion';

export type RdoActivityFilters = {
  stage: string;
  floor: string;
  vertical: string;
  system: string;
  device: string;
  plannedFrom: string;
  plannedTo: string;
  responsible: string;
  situation: string;
  onlyToday: boolean;
  /** Data de referência do RDO (YYYY-MM-DD) para filtro e destaques. */
  referenceDate?: string;
};

export const DEFAULT_ACTIVITY_FILTERS: RdoActivityFilters = {
  stage: '',
  floor: '',
  vertical: '',
  system: '',
  device: '',
  plannedFrom: '',
  plannedTo: '',
  responsible: '',
  situation: '',
  onlyToday: false,
};

const TASK_LABELS: Record<string, string> = {
  cabling: 'CABO',
  installation: 'INSTAL',
  configuration: 'CONFG',
};

export const QUANTITATIVE_TASK_TYPES = [
  'cabling',
  'installation',
  'configuration',
] as const;

export function hasQuantitativeTaskBreakdown(
  activity: Pick<RdoScheduleActivity, 'taskBreakdown'>,
): boolean {
  return (activity.taskBreakdown ?? []).some(
    (entry) =>
      entry.taskType != null &&
      QUANTITATIVE_TASK_TYPES.includes(
        entry.taskType as (typeof QUANTITATIVE_TASK_TYPES)[number],
      ),
  );
}

/** Sem qtde e sem tarefas Cab/Inst/Conf — apenas % de conclusão (ex.: comissionamento, start-up). */
export function usesCompletionOnlyInput(
  activity: Pick<
    RdoScheduleActivity,
    'taskBreakdown' | 'totalQuantity' | 'activityType'
  >,
): boolean {
  if (hasQuantitativeTaskBreakdown(activity)) {
    return false;
  }

  const hasActivityQuantity = (Number(activity.totalQuantity) || 0) > 0;
  if (hasActivityQuantity && activity.activityType === 'schedule') {
    return false;
  }

  return true;
}

const SITUATION_LABELS: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  blocked: 'Bloqueada',
  completed: 'Concluída',
};

function normalizePlannedDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value.slice(0, 10);
}

/** Valor usado no filtro/select de pavimento: descrição (nome), não o código. */
export function floorFilterValue(activity: RdoScheduleActivity): string | null {
  const description = activity.floor?.trim();
  if (description) {
    return description;
  }

  return activity.floorCode?.trim() || null;
}

function activityOverlapsPlannedRange(
  activity: RdoScheduleActivity,
  from: string,
  to: string,
): boolean {
  const start = normalizePlannedDate(activity.plannedStart);
  const finish = normalizePlannedDate(activity.plannedFinish) || start;
  const actStart = start || finish;
  const actFinish = finish || start;

  if (!actStart && !actFinish) {
    return false;
  }

  const rangeFrom = from || '0000-01-01';
  const rangeTo = to || '9999-12-31';

  return actStart <= rangeTo && actFinish >= rangeFrom;
}

export function resolveActivityReferenceDate(referenceDate?: string): string {
  return referenceDate?.slice(0, 10) || todayIsoDateLocal();
}

export function isPlannedForReferenceDate(
  activity: RdoScheduleActivity,
  referenceDate?: string,
): boolean {
  const refDate = resolveActivityReferenceDate(referenceDate);
  const start = normalizePlannedDate(activity.plannedStart);
  const finish = normalizePlannedDate(activity.plannedFinish);

  if (!start && !finish) {
    return false;
  }

  if (start && finish) {
    return start <= refDate && refDate <= finish;
  }

  return start === refDate || finish === refDate;
}

export function isOverdueForReferenceDate(
  activity: RdoScheduleActivity,
  referenceDate?: string,
): boolean {
  const finish = normalizePlannedDate(activity.plannedFinish);
  if (!finish) {
    return false;
  }

  return (
    finish < resolveActivityReferenceDate(referenceDate) &&
    activity.situation !== 'completed'
  );
}

export function filterScheduleActivities(
  activities: RdoScheduleActivity[],
  filters: RdoActivityFilters,
): RdoScheduleActivity[] {
  return activities.filter((activity) => {
    if (filters.stage && activity.stage) {
      if (!activity.stage.toUpperCase().includes(filters.stage.toUpperCase())) {
        return false;
      }
    } else if (filters.stage && !activity.stage) {
      const tagMatch = activity.tags.some((t) =>
        t.toUpperCase().includes(filters.stage.toUpperCase()),
      );
      if (!tagMatch) {
        return false;
      }
    }

    if (filters.floor) {
      const floorKey = floorFilterValue(activity);
      if (floorKey !== filters.floor) {
        return false;
      }
    }

    if (filters.vertical && activity.vertical !== filters.vertical) {
      return false;
    }

    if (filters.system && activity.system !== filters.system) {
      return false;
    }
    if (filters.device && activity.device !== filters.device) {
      return false;
    }

    if (filters.situation && activity.situation !== filters.situation) {
      return false;
    }

    if (filters.responsible) {
      const match = activity.responsibleNames.some((name) =>
        name.toLowerCase().includes(filters.responsible.toLowerCase()),
      );
      if (!match) {
        return false;
      }
    }

    if (filters.plannedFrom || filters.plannedTo) {
      if (
        !activityOverlapsPlannedRange(
          activity,
          filters.plannedFrom,
          filters.plannedTo,
        )
      ) {
        return false;
      }
    }

    if (
      filters.onlyToday &&
      !isPlannedForReferenceDate(activity, filters.referenceDate)
    ) {
      return false;
    }

    return true;
  });
}

export function uniqueFilterValues(
  activities: RdoScheduleActivity[],
  key: keyof Pick<
    RdoScheduleActivity,
    'stage' | 'floor' | 'vertical' | 'system' | 'device'
  >,
): string[] {
  const sortKeyByField: Partial<
    Record<
      typeof key,
      'stageSortOrder' | 'floorSortOrder' | 'verticalSortOrder' | 'systemSortOrder'
    >
  > = {
    stage: 'stageSortOrder',
    floor: 'floorSortOrder',
    vertical: 'verticalSortOrder',
    system: 'systemSortOrder',
  };

  const orderField = sortKeyByField[key];
  const bestOrder = new Map<string, number>();

  for (const activity of activities) {
    const value = key === 'floor' ? floorFilterValue(activity) : activity[key];
    if (!value) {
      continue;
    }

    const order = orderField
      ? scheduleSortValue(activity[orderField] as number | null | undefined)
      : SCHEDULE_SORT_FALLBACK;

    const current = bestOrder.get(value);
    if (current === undefined || order < current) {
      bestOrder.set(value, order);
    }
  }

  return [...bestOrder.entries()]
    .sort(([valueA, orderA], [valueB, orderB]) =>
      orderA - orderB || valueA.localeCompare(valueB, 'pt-BR'),
    )
    .map(([value]) => value);
}

export type ActivityStatusBadge = 'hoje' | 'ok' | 'vencido' | 'cron';

export type ActivityMetricCell = {
  real: number;
  prev: number;
};

export type ActivityRowMetrics = {
  cabo: ActivityMetricCell | null;
  instal: ActivityMetricCell | null;
  confg: ActivityMetricCell | null;
  completionPct: number;
};

export function activityStatusBadge(
  activity: RdoScheduleActivity,
  referenceDate?: string,
): ActivityStatusBadge {
  if (activity.situation === 'completed') {
    return 'ok';
  }

  const refDate = resolveActivityReferenceDate(referenceDate);

  if (isOverdueForReferenceDate(activity, refDate)) {
    return 'vencido';
  }

  if (isPlannedForReferenceDate(activity, refDate)) {
    return 'hoje';
  }

  return 'cron';
}

const BADGE_STYLES: Record<
  ActivityStatusBadge,
  { label: string; badge: string; bar: string }
> = {
  hoje: {
    label: 'HOJE',
    badge: 'bg-amber-100 text-amber-800',
    bar: 'bg-amber-400',
  },
  ok: {
    label: 'OK',
    badge: 'bg-emerald-100 text-emerald-800',
    bar: 'bg-emerald-500',
  },
  vencido: {
    label: 'VENCIDO',
    badge: 'bg-red-100 text-red-800',
    bar: 'bg-red-500',
  },
  cron: {
    label: 'CRON',
    badge: 'bg-sky-100 text-sky-800',
    bar: 'bg-sky-500',
  },
};

export function statusBadgeStyle(badge: ActivityStatusBadge) {
  return BADGE_STYLES[badge];
}

export type ProgressBadgeLevel = 'low' | 'mid' | 'high';

export function progressBadgeFromPct(pct: number): ProgressBadgeLevel {
  if (pct >= 80) {
    return 'high';
  }
  if (pct >= 50) {
    return 'mid';
  }
  return 'low';
}

const PROGRESS_BADGE_STYLES: Record<
  ProgressBadgeLevel,
  { label: string; badge: string; bar: string }
> = {
  low: {
    label: 'PEND',
    badge: 'bg-slate-200 text-slate-700',
    bar: 'bg-slate-400',
  },
  mid: {
    label: 'PARC',
    badge: 'bg-amber-100 text-amber-800',
    bar: 'bg-amber-400',
  },
  high: {
    label: 'OK',
    badge: 'bg-emerald-100 text-emerald-800',
    bar: 'bg-emerald-500',
  },
};

export function progressBadgeStyle(level: ProgressBadgeLevel) {
  return PROGRESS_BADGE_STYLES[level];
}

export type PhaseKey = 'cabo' | 'instal' | 'confg';

export type ExpandedActivityRow = {
  key: string;
  activity: RdoScheduleActivity;
  phase: PhaseKey;
  /** Rótulo principal a exibir no lugar do título — fase ou title original. */
  label: string;
  metric: ActivityMetricCell;
};

const PHASE_LABELS: Record<PhaseKey, string> = {
  cabo: 'Cabeamento',
  instal: 'Instalação',
  confg: 'Configuração',
};

const TASK_TYPE_TO_PHASE_KEY: Record<string, PhaseKey> = {
  cabling: 'cabo',
  installation: 'instal',
  configuration: 'confg',
};

/**
 * Quebra uma atividade em sub-linhas, uma por tarefa filha (cabeamento, instalação,
 * configuração). A linha de cabeçalho representando a própria atividade é renderizada
 * separadamente no caller.
 *
 * - Se a atividade tem `taskBreakdown` (esperado para o nível ACTIVITY), gera
 *   1 linha por task filha, usando os totais reais de cada uma.
 * - Para atividades folha (sem tasks), gera 1 linha "total" com as quantidades
 *   da própria atividade.
 */
export function expandActivityRows(
  activity: RdoScheduleActivity,
): ExpandedActivityRow[] {
  if (!activity.taskBreakdown?.length) {
    return [];
  }

  return activity.taskBreakdown
    .map((entry) => {
      const phaseKey = entry.taskType
        ? TASK_TYPE_TO_PHASE_KEY[entry.taskType]
        : undefined;
      if (!phaseKey) {
        return null;
      }

      return {
        key: `${activity.id}-${entry.taskId}`,
        activity,
        phase: phaseKey,
        label: PHASE_LABELS[phaseKey],
        metric: {
          real: Number(entry.actualQuantity) || 0,
          prev: Number(entry.totalQuantity) || 0,
        },
      } as ExpandedActivityRow;
    })
    .filter((row): row is ExpandedActivityRow => row !== null);
}

export function phaseLivePct(metric: ActivityMetricCell, apontar: number): number {
  if (metric.prev <= 0) {
    return 0;
  }

  return Math.min(
    100,
    Math.max(0, Math.round(((metric.real + apontar) / metric.prev) * 100)),
  );
}

export function liveCompletionPct(
  activity: RdoScheduleActivity,
  apontar: { cabo: number; instal: number; confg: number },
): number {
  return liveActivityCompletionPct(activity, apontar);
}

export function activityRowMetrics(
  activity: RdoScheduleActivity,
): ActivityRowMetrics {
  return {
    cabo: phaseCellFor(activity, 'cabo'),
    instal: phaseCellFor(activity, 'instal'),
    confg: phaseCellFor(activity, 'confg'),
    completionPct:
      usesCompletionOnlyInput(activity) && activity.completionPercentage != null
        ? Math.round(Number(activity.completionPercentage))
        : Math.round(
            liveCompletionPct(activity, { cabo: 0, instal: 0, confg: 0 }),
          ),
  };
}

const PHASE_TO_TASK_TYPE: Record<PhaseKey, string> = {
  cabo: 'cabling',
  instal: 'installation',
  confg: 'configuration',
};

/**
 * Returns the Real/Prev cell for a given phase only when the activity has a
 * linked child task (cabling / installation / configuration) in taskBreakdown.
 */
export function phaseCellFor(
  activity: RdoScheduleActivity,
  phase: PhaseKey,
): ActivityMetricCell | null {
  const taskType = PHASE_TO_TASK_TYPE[phase];
  const entry = activity.taskBreakdown?.find((t) => t.taskType === taskType);

  if (!entry) {
    return null;
  }

  return {
    real: Number(entry.actualQuantity) || 0,
    prev: Number(entry.totalQuantity) || 0,
  };
}

export function activityHasLinkedTaskPhases(
  activity: RdoScheduleActivity,
): boolean {
  return (activity.taskBreakdown ?? []).some(
    (entry) =>
      entry.taskType
      && TASK_TYPE_TO_PHASE_KEY[entry.taskType] !== undefined,
  );
}

/** Saldo disponível para apontar na fase (previsto − realizado). */
export function remainingApontarBalance(
  cell: ActivityMetricCell | null,
): number {
  if (!cell) {
    return 0;
  }

  return Math.max(0, (cell.prev || 0) - (cell.real || 0));
}

export function maxApontarForPhase(
  activity: RdoScheduleActivity,
  phase: PhaseKey,
): number {
  return remainingApontarBalance(phaseCellFor(activity, phase));
}

export function parseApontarInputValue(value: unknown): number {
  return Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0);
}

export function isApontarWithinBalance(
  activity: RdoScheduleActivity,
  phase: PhaseKey,
  value: number,
): boolean {
  return value <= maxApontarForPhase(activity, phase);
}

/** Retorna a quantidade normalizada ou `null` se exceder o saldo (não aplica clamp). */
export function tryApplyApontarQuantity(
  activity: RdoScheduleActivity,
  phase: PhaseKey,
  rawValue: unknown,
): number | null {
  const max = maxApontarForPhase(activity, phase);
  if (max <= 0) {
    return 0;
  }

  const value = parseApontarInputValue(rawValue);
  if (value > max) {
    return null;
  }

  return value;
}

export function clampApontarQuantity(
  activity: RdoScheduleActivity,
  phase: PhaseKey,
  value: number,
): number {
  const max = maxApontarForPhase(activity, phase);
  const normalized = parseApontarInputValue(value);

  return Math.min(normalized, max);
}

function readTaskEstimatedTimeHours(entry: RdoTaskBreakdownEntry): number {
  const raw =
    entry.estimatedTimeHours ??
    (entry as { estimated_time_hours?: number | null }).estimated_time_hours;

  return Number(raw) || 0;
}

function quantitativeBreakdownEntries(
  activity: RdoScheduleActivity,
): RdoTaskBreakdownEntry[] {
  return (activity.taskBreakdown ?? []).filter(
    (entry) =>
      entry.taskType != null &&
      QUANTITATIVE_TASK_TYPES.includes(
        entry.taskType as (typeof QUANTITATIVE_TASK_TYPES)[number],
      ),
  );
}

function taskLiveCompletionPct(
  entry: RdoTaskBreakdownEntry,
  apontar: { cabo: number; instal: number; confg: number },
): number {
  const phaseKey = entry.taskType
    ? TASK_TYPE_TO_PHASE_KEY[entry.taskType]
    : undefined;
  const apontarQty = phaseKey ? apontar[phaseKey] || 0 : 0;
  const prev = Number(entry.totalQuantity) || 0;

  if (prev > 0) {
    const real = (Number(entry.actualQuantity) || 0) + apontarQty;
    return Math.min(100, Math.max(0, (real / prev) * 100));
  }

  return Math.min(
    100,
    Math.max(0, Number(entry.completionPercentage) || 0),
  );
}

/**
 * Percentual ao vivo da atividade, ponderado pelas horas previstas de cada tarefa
 * (Cab / Inst / Conf), alinhado ao ScheduleRecalculationService.
 */
export function liveActivityCompletionPct(
  activity: RdoScheduleActivity,
  apontar: { cabo: number; instal: number; confg: number },
): number {
  if (
    usesCompletionOnlyInput(activity) &&
    activity.completionPercentage != null
  ) {
    return Math.min(100, Math.max(0, Math.round(Number(activity.completionPercentage))));
  }

  const breakdown = quantitativeBreakdownEntries(activity);
  if (!breakdown.length) {
    return activity.completionPercentage != null
      ? Math.round(Number(activity.completionPercentage))
      : 0;
  }

  const children = breakdown.map((entry) => ({
    completion_percentage: taskLiveCompletionPct(entry, apontar),
    estimated_time_hours: readTaskEstimatedTimeHours(entry),
    total_value: 0,
  }));

  return Math.round(calculateParentCompletion(children));
}

/**
 * Aggregates Real/Prev across a list of activities for a given phase.
 * Useful for rendering subtotals at the group header.
 */
export function aggregatePhaseCell(
  activities: RdoScheduleActivity[],
  phase: PhaseKey,
): ActivityMetricCell {
  let real = 0;
  let prev = 0;
  for (const activity of activities) {
    const cell = phaseCellFor(activity, phase);
    if (cell) {
      real += cell.real;
      prev += cell.prev;
    }
  }
  return { real, prev };
}

/** Rótulo de agrupamento por etapa do cronograma (mobilização, planejamento, etc.). */
export function activityGroupLabel(activity: RdoScheduleActivity): string {
  const stage = (activity.stage ?? '').trim();

  return stage ? stage.toUpperCase() : 'SEM ETAPA';
}

/** Caminho abaixo da etapa: pavimento / vertical / sistema (formato anterior do lançamento). */
export function activityPathLabel(activity: RdoScheduleActivity): string {
  const floorPart = floorFilterValue(activity);
  const parts = [floorPart, activity.vertical, activity.system].filter(Boolean);

  return parts.length > 0
    ? parts.join(' / ').toUpperCase()
    : 'OUTRAS ATIVIDADES';
}

export type ScheduleActivityPathGroup = {
  key: string;
  items: RdoScheduleActivity[];
};

export type ScheduleActivityStageGroup = {
  key: string;
  subgroups: ScheduleActivityPathGroup[];
};

/** Atividades exibíveis no RDO de campo — exclui tipo administrativa. */
export function isRdoFieldActivity(
  activity: Pick<RdoScheduleActivity, 'activityType'>,
): boolean {
  return activity.activityType !== 'administrative';
}

export function filterRdoFieldActivities(
  activities: RdoScheduleActivity[],
): RdoScheduleActivity[] {
  return activities.filter(isRdoFieldActivity);
}

export function atividadeStageLabel(act: RdoAtividade): string {
  const stage = (act.stage ?? '').trim();
  if (stage) {
    return stage.toUpperCase();
  }

  const firstTag = act.tags[0]?.trim();
  return firstTag ? firstTag.toUpperCase() : 'SEM ETAPA';
}

export function groupAtividadesByStage(
  atividades: RdoAtividade[],
): { key: string; items: RdoAtividade[] }[] {
  const map = new Map<string, RdoAtividade[]>();

  for (const act of atividades) {
    const key = atividadeStageLabel(act);
    const list = map.get(key) ?? [];
    list.push(act);
    map.set(key, list);
  }

  return [...map.entries()]
    .sort(([, itemsA], [, itemsB]) => {
      const sortA = scheduleSortValue(itemsA[0]?.stageSortOrder);
      const sortB = scheduleSortValue(itemsB[0]?.stageSortOrder);

      if (sortA !== sortB) {
        return sortA - sortB;
      }

      const titleA = itemsA[0]?.title ?? '';
      const titleB = itemsB[0]?.title ?? '';

      return titleA.localeCompare(titleB, 'pt-BR');
    })
    .map(([key, items]) => ({ key, items }));
}

const SCHEDULE_SORT_FALLBACK = 999_999;

function scheduleSortValue(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : SCHEDULE_SORT_FALLBACK;
}

/** Ordem hierárquica do cronograma (etapa → pavimento → vertical → sistema → atividade). */
export function compareScheduleActivities(
  a: RdoScheduleActivity,
  b: RdoScheduleActivity,
): number {
  const keys = [
    scheduleSortValue(a.stageSortOrder),
    scheduleSortValue(b.stageSortOrder),
  ];
  if (keys[0] !== keys[1]) {
    return keys[0] - keys[1];
  }

  const floorA = scheduleSortValue(a.floorSortOrder);
  const floorB = scheduleSortValue(b.floorSortOrder);
  if (floorA !== floorB) {
    return floorA - floorB;
  }

  const verticalA = scheduleSortValue(a.verticalSortOrder);
  const verticalB = scheduleSortValue(b.verticalSortOrder);
  if (verticalA !== verticalB) {
    return verticalA - verticalB;
  }

  const systemA = scheduleSortValue(a.systemSortOrder);
  const systemB = scheduleSortValue(b.systemSortOrder);
  if (systemA !== systemB) {
    return systemA - systemB;
  }

  const activityA = scheduleSortValue(a.sortOrder);
  const activityB = scheduleSortValue(b.sortOrder);
  if (activityA !== activityB) {
    return activityA - activityB;
  }

  const deviceA = (a.device ?? a.title ?? '').toLowerCase();
  const deviceB = (b.device ?? b.title ?? '').toLowerCase();
  const byDevice = deviceA.localeCompare(deviceB, 'pt-BR');
  if (byDevice !== 0) {
    return byDevice;
  }

  return taskTypeOrder(a) - taskTypeOrder(b);
}

export function sortScheduleActivities(
  activities: RdoScheduleActivity[],
): RdoScheduleActivity[] {
  return [...activities].sort(compareScheduleActivities);
}

const TASK_TYPE_ORDER: Record<string, number> = {
  cabling: 1,
  installation: 2,
  configuration: 3,
};

function taskTypeOrder(activity: RdoScheduleActivity): number {
  return activity.taskType ? (TASK_TYPE_ORDER[activity.taskType] ?? 9) : 9;
}

export function groupScheduleActivities(
  activities: RdoScheduleActivity[],
): { key: string; items: RdoScheduleActivity[] }[] {
  const map = new Map<string, RdoScheduleActivity[]>();

  for (const activity of activities) {
    const key = activityGroupLabel(activity);
    const list = map.get(key) ?? [];
    list.push(activity);
    map.set(key, list);
  }

  return [...map.entries()]
    .sort(([, itemsA], [, itemsB]) => {
      const firstA = itemsA[0];
      const firstB = itemsB[0];
      if (!firstA || !firstB) {
        return 0;
      }

      const stageCmp =
        scheduleSortValue(firstA.stageSortOrder) -
        scheduleSortValue(firstB.stageSortOrder);
      if (stageCmp !== 0) {
        return stageCmp;
      }

      return compareScheduleActivities(firstA, firstB);
    })
    .map(([key, items]) => ({
      key,
      items: sortScheduleActivities(items),
    }));
}

/** Etapa no topo; abaixo, caminho pavimento / vertical / sistema (lançar atividade). */
export function groupScheduleActivitiesByStageAndPath(
  activities: RdoScheduleActivity[],
): ScheduleActivityStageGroup[] {
  const stageMap = new Map<string, Map<string, RdoScheduleActivity[]>>();

  for (const activity of activities) {
    const stageKey = activityGroupLabel(activity);
    const pathKey = activityPathLabel(activity);
    const pathMap = stageMap.get(stageKey) ?? new Map<string, RdoScheduleActivity[]>();
    const list = pathMap.get(pathKey) ?? [];
    list.push(activity);
    pathMap.set(pathKey, list);
    stageMap.set(stageKey, pathMap);
  }

  return [...stageMap.entries()]
    .sort(([, pathsA], [, pathsB]) => {
      const firstA = pathsA.values().next().value?.[0];
      const firstB = pathsB.values().next().value?.[0];
      if (!firstA || !firstB) {
        return 0;
      }

      const stageCmp =
        scheduleSortValue(firstA.stageSortOrder) -
        scheduleSortValue(firstB.stageSortOrder);
      if (stageCmp !== 0) {
        return stageCmp;
      }

      return compareScheduleActivities(firstA, firstB);
    })
    .map(([stageKey, pathMap]) => ({
      key: stageKey,
      subgroups: [...pathMap.entries()]
        .sort(([, itemsA], [, itemsB]) => {
          const firstA = itemsA[0];
          const firstB = itemsB[0];
          if (!firstA || !firstB) {
            return 0;
          }

          return compareScheduleActivities(firstA, firstB);
        })
        .map(([pathKey, items]) => ({
          key: pathKey,
          items: sortScheduleActivities(items),
        })),
    }));
}

export function activityRowClass(
  activity: RdoScheduleActivity,
  referenceDate?: string,
): string {
  const badge = activityStatusBadge(activity, referenceDate);

  if (badge === 'hoje') {
    return 'bg-amber-50/80';
  }
  if (badge === 'vencido') {
    return 'bg-red-50/80';
  }
  if (badge === 'ok') {
    return 'bg-emerald-50/50';
  }

  return 'bg-card';
}

export function apontarInputClass(
  activity: RdoScheduleActivity,
  referenceDate?: string,
): string {
  const badge = activityStatusBadge(activity, referenceDate);
  if (badge === 'hoje') {
    return 'border-amber-400 focus-visible:border-amber-500';
  }
  if (badge === 'vencido') {
    return 'border-red-400 focus-visible:border-red-500';
  }

  return '';
}

function phasesFromBreakdown(
  activity: RdoScheduleActivity,
): RdoAtividadePhase[] {
  const breakdown: RdoTaskBreakdownEntry[] = activity.taskBreakdown ?? [];

  return breakdown
    .filter((entry) => entry.taskType && TASK_LABELS[entry.taskType])
    .map((entry) => ({
      scheduleItemId: entry.taskId,
      taskType: entry.taskType as string,
      label: TASK_LABELS[entry.taskType as string] ?? (entry.taskType ?? '').toUpperCase(),
      current: Number(entry.actualQuantity) || 0,
      total: Number(entry.totalQuantity) || 0,
      estimatedTimeHours: readTaskEstimatedTimeHours(entry),
    }));
}

function buildProgress(activity: RdoScheduleActivity): RdoAtividade['progress'] {
  const phases = phasesFromBreakdown(activity);
  if (!phases.length) {
    return [];
  }

  return phases.map(({ label, current, total }) => ({
    label,
    current,
    total: total || Math.max(current, 1),
  }));
}

export function scheduleActivityToRdoAtividade(
  activity: RdoScheduleActivity,
  apontar?: Partial<RdoAtividade['apontar']>,
): RdoAtividade {
  const situation = (activity.situation ?? 'pending') as RdoAtividadeSituacao;
  const isPendingType = usesCompletionOnlyInput(activity);

  return {
    id: generateFieldId(),
    scheduleItemId: activity.id,
    stage: activity.stage,
    stageSortOrder: activity.stageSortOrder,
    tags: activity.tags,
    title: activity.title,
    status: SITUATION_LABELS[situation] ?? activity.situationLabel,
    situation,
    activityType: activity.activityType,
    taskType: activity.taskType,
    device: activity.device,
    isPendingType,
    progress: buildProgress(activity),
    phases: phasesFromBreakdown(activity),
    apontar: {
      cabo: apontar?.cabo ?? 0,
      instal: apontar?.instal ?? 0,
      confg: apontar?.confg ?? 0,
    },
    apontamentos: [],
    completionPercentage: isPendingType
      ? (activity.completionPercentage ?? 0)
      : null,
  };
}

export function useRdoActivityFilters(activities: Ref<RdoScheduleActivity[]>) {
  const stageOptions = computed(() =>
    uniqueFilterValues(activities.value, 'stage'),
  );
  const floorOptions = computed(() =>
    uniqueFilterValues(activities.value, 'floor'),
  );
  const verticalOptions = computed(() =>
    uniqueFilterValues(activities.value, 'vertical'),
  );
  const systemOptions = computed(() =>
    uniqueFilterValues(activities.value, 'system'),
  );
  const deviceOptions = computed(() =>
    uniqueFilterValues(activities.value, 'device'),
  );

  return {
    stageOptions,
    floorOptions,
    verticalOptions,
    systemOptions,
    deviceOptions,
  };
}

export function hasActiveActivityFilters(filters: RdoActivityFilters): boolean {
  return Boolean(
    filters.stage ||
      filters.floor ||
      filters.vertical ||
      filters.system ||
      filters.device ||
      filters.plannedFrom ||
      filters.plannedTo ||
      filters.responsible ||
      filters.situation,
  );
}

export function shouldApplyActivityFilters(filters: RdoActivityFilters): boolean {
  return hasActiveActivityFilters(filters) || filters.onlyToday;
}
